import { type ReactNode } from 'react';
import { Head } from 'vite-react-ssg/single-page';
import { SiteHeader, type SiteHeaderProps } from './SiteHeader';

export interface MarketingPageHead {
    title: string;
    description: string;
    canonical: string;
    jsonLd?: object | null;
    lang?: string;
    alternates?: ReadonlyArray<{ hreflang: string; href: string }>;
}

interface MarketingPageProps {
    head: MarketingPageHead;
    /** The site top bar's options (a landing page passes its locale words + the open-file hook). */
    siteHeader?: SiteHeaderProps;
    children: ReactNode;
}

/**
 * Wrapper for all marketing pages. Injects `<Head>` metadata (title, description,
 * canonical, og/twitter tags, optional JSON-LD) from a config object so page
 * content is the only thing each page file needs to define. Every marketing page opens with the site top bar
 * (SiteHeader), its own page marked current.
 * No data-theme pin -- uses semantic tokens for both light and dark modes.
 */
export function MarketingPage({ head, siteHeader, children }: MarketingPageProps) {
    return (
        <>
            <Head>
                {head.lang && <html lang={head.lang} />}
                <title>{head.title}</title>
                <meta name="description" content={head.description} />
                <link rel="canonical" href={head.canonical} />
                <meta property="og:title" content={head.title} />
                <meta property="og:description" content={head.description} />
                <meta property="og:url" content={head.canonical} />
                <meta name="twitter:title" content={head.title} />
                <meta name="twitter:description" content={head.description} />
                {head.alternates?.map((alt) => (
                    <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
                ))}
                {head.jsonLd != null && (
                    <script type="application/ld+json">
                        {JSON.stringify(head.jsonLd)}
                    </script>
                )}
            </Head>
            <div className="mktg-page">
                <SiteHeader currentPath={new URL(head.canonical).pathname} {...siteHeader} />
                {children}
            </div>
        </>
    );
}
